import ChatbotIcon from '@/components/ui/ChatbotIcon';
import useDeleteChatbot from '@/hooks/useDeleteChatbot';
import { api } from '@/lib/trpc/react';
import {
  type FileStatus,
  clearUploadingFilesAtom,
  isUploadingAtom,
  updateUploadingFileAtom,
  uploadingFilesAtom,
} from '@/store/fileUploadStore';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  Check,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  CircleDashed,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useWorkspaceActions } from '@/hooks/useWorkspace';
import { cn } from '@/utils/utils';
import type { ChatbotType } from '@unriddle/db/types';
import { useRouter } from 'next/router';
import { Button } from '../Button/Button';
import { Icon } from '../ui/Icon';
import { useNewSummary } from './useUpdateSummary';

const StatusIcon = ({ status }: { status: FileStatus }) => {
  switch (status) {
    case 'IN_PROGRESS':
      return (
        <div className="h-4 w-4 loader-small-gray text-muted-foreground" />
      );
    case 'SUCCESS_PENDING_IMAGES':
    case 'SUCCESS':
      return <Check className="h-4 w-4 text-blue" strokeWidth={3} />;
    case 'FAILED':
    case 'TIMEOUT':
      return (
        <CircleAlert className="h-4 w-4 text-red-500 text-destructive-solid" />
      );
    default:
      return <CircleDashed className="h-4 w-4 text-muted-foreground" />;
  }
};

interface FileUploadToastContentProps {
  toastId: string | number;
}

export const UPLOAD_TOAST_ID = 'file-upload-progress-toast';

export function FileUploadToastContent({
  toastId,
}: FileUploadToastContentProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [uploadingFiles] = useAtom(uploadingFilesAtom);
  const isUploading = useAtomValue(isUploadingAtom);
  const updateFile = useSetAtom(updateUploadingFileAtom);
  const clearFiles = useSetAtom(clearUploadingFilesAtom);
  const { mutateDelete: deleteChatbot } = useDeleteChatbot();
  const { updateSummary } = useNewSummary();

  const router = useRouter();

  const { updateChatbot } = useWorkspaceActions();

  useEffect(() => {
    if (isUploading === 0 && uploadingFiles.length > 0) {
      const allSuccessful = uploadingFiles.every(
        (f) => f.status === 'SUCCESS' || f.status === 'SUCCESS_PENDING_IMAGES',
      );
      if (allSuccessful) {
        setTimeout(() => {
          clearFiles();
          toast.dismiss(toastId);
        }, 5000);
      }
    }
  }, [isUploading, uploadingFiles, toastId]);

  const chatbotIds = uploadingFiles
    .filter(
      (f) =>
        f.status !== 'SUCCESS' &&
        f.status !== 'SUCCESS_PENDING_IMAGES' &&
        f.chatbotId,
    )
    .map((f) => f.chatbotId) as string[];

  const successfulFilesCount = uploadingFiles.filter(
    (f) => f.status === 'SUCCESS' || f.status === 'SUCCESS_PENDING_IMAGES',
  ).length;

  const headerText =
    isUploading > 0
      ? `Uploading ${isUploading} ${isUploading === 1 ? 'file' : 'files'}`
      : `${successfulFilesCount} ${successfulFilesCount === 1 ? 'file' : 'files'} uploaded`;
  const { data: chatbotStatuses } = api.chatbot.ingestionStatuses.useQuery(
    chatbotIds,
    {
      enabled: !!chatbotIds.length,
      refetchInterval: (query) => {
        const statuses = query.state.data;
        if (!statuses) return 2000;

        const hasProcessing = statuses.some(
          (chatbot) =>
            !chatbot.isIndexed ||
            chatbot.ingestionStatus?.status === 'IN_PROGRESS',
        );

        if (!hasProcessing) return false;

        const count = query.state.dataUpdateCount;
        if (count > 15) return false;
        if (count > 10) return 10000;
        if (count > 5) return 6000;

        return 2000;
      },
    },
  );

  useEffect(() => {
    if (!chatbotStatuses) return;
    for (const chatbot of chatbotStatuses) {
      const fileToUpdate = uploadingFiles.find(
        (f) => f.chatbotId === chatbot.id,
      );

      if (fileToUpdate && (chatbot.ingestionStatus || chatbot.isIndexed)) {
        const ingestionStatus = chatbot.ingestionStatus;
        const updates: {
          id: string;
          status: FileStatus;
          errorMessage?: string;
          name?: string;
          type?: ChatbotType;
        } = {
          id: fileToUpdate?.id,
          status: ingestionStatus?.status,
          errorMessage: ingestionStatus?.failureReason ?? undefined,
        };

        if (chatbot.name && chatbot.name !== fileToUpdate.name) {
          updates.name = chatbot.name;
        }
        if (chatbot.isIndexed) {
          updates.status = 'SUCCESS';
        }

        if (updates.status === 'SUCCESS') {
          updateSummary(chatbot.id);
        }

        if (chatbot.type && chatbot.type !== fileToUpdate.type) {
          updates.type = chatbot.type;
        }

        updateFile(updates);
      }

      updateChatbot(chatbot.id, {
        isIndexed: chatbot.isIndexed,
        ingestionStatus: chatbot.ingestionStatus?.status,
        documentUrl: chatbot.documentUrl,
        name: chatbot.name,
        type: chatbot.type,
        url: chatbot.url,
      });
    }
  }, [chatbotStatuses]);

  const handleCancelAllAndDismiss = () => {
    const inProgressChatbots = uploadingFiles
      .filter((f) => f.status === 'IN_PROGRESS' && f.chatbotId)
      .map((f) => ({ id: f.chatbotId as string }));

    if (inProgressChatbots.length) {
      deleteChatbot(inProgressChatbots);
    }

    clearFiles();
    toast.dismiss(toastId);
  };

  const toggleOpen = () => setIsOpen((prev) => !prev);

  return (
    <div className="w-full max-w-md rounded-lg bg-popover shadow-feint overflow-hidden text-foreground border border-border">
      <div className="flex items-center justify-between p-2">
        <span className="text-base leading-[100%] font-semibold pl-2">
          {headerText}
        </span>
        <div className="flex items-center gap-1">
          {isUploading > 0 ? (
            <>
              <Button
                variant="outline"
                size="xs"
                className="h-7"
                onClick={handleCancelAllAndDismiss}
              >
                Cancel
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={toggleOpen}
              >
                {isOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleCancelAllAndDismiss}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div
        className={`
          overflow-y-auto
          transition-all
          duration-300
          ease-in-out
          ${isOpen && uploadingFiles.length > 0 ? 'max-h-72 opacity-100' : 'max-h-0 opacity-0'}
        `}
      >
        <div
          className={`
          py-2
          mx-2
          overflow-y-auto
          transition-transform
          duration-300
          ease-in-out
          ${isOpen && uploadingFiles.length > 0 ? 'translate-y-0' : '-translate-y-2'}
        `}
        >
          <ul>
            {uploadingFiles.map((file) => (
              <li
                key={file.id}
                className={cn(
                  'flex items-center justify-between gap-2 px-2 py-1 rounded-lg',
                  (file.status === 'FAILED' || file.status === 'TIMEOUT') &&
                    'text-destructive-solid',
                  (file.status === 'SUCCESS' ||
                    file.status === 'SUCCESS_PENDING_IMAGES') &&
                    'hover:bg-muted hover:cursor-pointer',
                )}
                onClick={() => {
                  if (
                    file.status !== 'SUCCESS' &&
                    file.status !== 'SUCCESS_PENDING_IMAGES'
                  ) {
                    return;
                  }
                  router.push(`/b/${file.chatbotId}`);
                }}
              >
                <div className="flex min-w-0 items-center gap-2">
                  <Icon
                    as={ChatbotIcon({
                      type: file.type || 'DOCUMENT',
                    })}
                  />
                  <span className="flex-1 truncate text-sm font-medium">
                    {file.name}
                  </span>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <StatusIcon status={file.status} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
